import { useGetTournamentQuery } from 'store/services/play/playApi';
import type { TournamentState } from 'types/interfaces/play';
import { playErrorCode } from 'utils/play/playErrors';
import { useIsAuthenticated } from '../PlayRoutes';
import { usePlaySocket } from './usePlaySocket';

export interface TournamentQueryState {
  code: string;
  state: TournamentState | undefined;
  isLoading: boolean;
  isFetching: boolean;
  /** The backend does not know the code */
  notFound: boolean;
  error: unknown;
  isAuthenticated: boolean;
  refetch: () => void;
}

/**
 * One GET /play/tournaments/:code per code drives every Play screen for it.
 * Mutations invalidate the tag; the socket hook (G8) patches the same cache
 * between refetches. Auth is optional on the endpoint, so a logged-out visitor
 * gets the invite view with viewer.role NONE.
 */
export const useTournament = (code: string): TournamentQueryState => {
  const isAuthenticated = useIsAuthenticated();
  const { data, isLoading, isFetching, error, refetch } = useGetTournamentQuery(code);
  const status = (error as { status?: number } | undefined)?.status;
  const notFound = status === 404 || playErrorCode(error) === 'TOURNAMENT_NOT_FOUND';
  usePlaySocket({ code, enabled: !!data });
  return { code, state: data, isLoading, isFetching, notFound, error, isAuthenticated, refetch };
};
